import { useEffect, useSyncExternalStore } from 'react';
import type { Helix2000Character } from '../../../../shared/CharacterTypes';

interface DisplayFocus { journalId: string | null; trackerId: string | null }
const EMPTY: DisplayFocus = { journalId: null, trackerId: null };
let owner: string | null = null;
let focus = EMPTY;
let activeMembership = new Set<string>();
let journalMembership = new Set<string>();
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => listeners.delete(listener); };
function publish(next: DisplayFocus): void {
  if (next.journalId === focus.journalId && next.trackerId === focus.trackerId) return;
  focus = next;
  for (const listener of listeners) listener();
}

/** Local display preference only: never a quest mutation, wire message or persisted tracking record. */
export function questJournalOwner(player?: Helix2000Character | null): string {
  return player?._id || player?.id || '';
}
export function focusJournalQuest(characterOwner: string, questId: string): boolean {
  if (!characterOwner || owner !== characterOwner || !journalMembership.has(questId)) return false;
  publish({ ...focus, journalId: questId });
  return true;
}
export function focusTrackerQuest(characterOwner: string, questId: string | null): void {
  if (!characterOwner || owner !== characterOwner) return;
  if (questId !== null && !activeMembership.has(questId)) return;
  publish({ ...focus, trackerId: questId });
}

export function useQuestJournalFocus(characterOwner: string, activeIds: readonly string[], allIds: readonly string[]): DisplayFocus {
  const membership = JSON.stringify([activeIds, allIds]);
  useEffect(() => {
    if (owner !== characterOwner) {
      owner = characterOwner || null;
      focus = EMPTY;
      for (const listener of listeners) listener();
    }
    activeMembership = new Set(activeIds);
    journalMembership = new Set(allIds);
    publish({ journalId: focus.journalId && allIds.includes(focus.journalId) ? focus.journalId : null,
      trackerId: focus.trackerId && activeIds.includes(focus.trackerId) ? focus.trackerId : null });
  }, [characterOwner, membership]);
  return useSyncExternalStore(subscribe, () => owner === characterOwner ? focus : EMPTY, () => EMPTY);
}

export function resetQuestJournalFocusForTests(): void {
  owner = null;
  focus = EMPTY;
  activeMembership.clear();
  journalMembership.clear();
  for (const listener of listeners) listener();
}
